import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Header } from "@/components/Header";
import { usePublicSettings } from "@/hooks/useSettings";
import { useAuth } from "@/context/AuthContext";
import { usePlugins } from "@/plugins/usePlugins";
import { GuidedTour } from "@/components/tour/GuidedTour";
import { LANDING_TOUR } from "@/components/tour/tourSteps";

const DEFAULT_TITLE = "Type any topic and instantly get a narrated, **custom course** and build your **\"PersonalPedia\".**";
const DEFAULT_SUBTITLE = "Choose a **Two Minute Overview** or **15 minute Lunch break** or **Comprehensive Module based Training**.";

function renderEmphasis(text, cls) {
  return (text || "").split("**").map((seg, i) =>
    i % 2 === 1 ? <span key={i} className={cls}>{seg}</span> : <span key={i}>{seg}</span>
  );
}
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Zap, Coffee, Layers, ArrowRight, Play, History, Sparkles, Sprout, Volume2, VolumeX, ShieldCheck, Baby } from "lucide-react";
import { useDojo } from "@/plugins/vibeDojo/dojoAudio";
import { useDojoMode } from "@/plugins/vibeDojo/DojoContext";
import { EarlyAccessModal } from "@/components/EarlyAccessModal";

const PLANS = [
  { key: "overview", icon: Zap, time: "2 min", title: "Quick Overview", desc: "Get the gist of any topic in a punchy 2-minute narrated explainer." },
  { key: "lunch", icon: Coffee, time: "15 min", title: "Lunch Break Course", desc: "A focused, structured course you can finish over lunch." },
  { key: "comprehensive", icon: Layers, time: "Deep dive", title: "Comprehensive Training", desc: "Full module-based training that takes you from zero to confident." },
];

export default function Landing() {
  const [topic, setTopic] = useState("");
  const [exploreHover, setExploreHover] = useState(false);
  const [earlyOpen, setEarlyOpen] = useState(false);
  const [pendingParams, setPendingParams] = useState("");
  const [kidMode, setKidMode] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { enabled: dojoOn, enable: enterDojo, toggle: toggleDojo } = useDojo();
  const { setDojoMode } = useDojoMode();
  const { isPluginEnabled } = usePlugins();
  const vibeOn = isPluginEnabled("dojo_vibe");
  const { data: settings } = usePublicSettings();
  const landingTitle = settings?.landing?.title || DEFAULT_TITLE;
  const landingSubtitle = settings?.landing?.subtitle ?? DEFAULT_SUBTITLE;
  const searchPlaceholder = settings?.landing?.search_placeholder || "What do you want to master in 2 minutes?";
  const playlistHeading = settings?.landing?.playlist_heading || "Featured courses, on the house.";
  const xm = settings?.expertmeai || {};
  const xmEyebrow = xm.banner_eyebrow || "Featured Exhibit";
  const xmHeadline = xm.banner_headline || "Want to learn from the best?";
  const xmSubtitle = xm.banner_subtitle || "Explore ExpertMeAi — authentic mentorship from the world's greatest minds.";
  const xmCta = xm.banner_cta || "Explore Experts";

  const start = (length) => {
    const t = topic.trim();
    if (user) {
      const params = new URLSearchParams();
      if (t) params.set("topic", t);
      if (length) params.set("length", length);
      if (kidMode && length === "overview") params.set("kids", "1");
      navigate(`/learn?${params.toString()}`, { state: { topic: t, length } });
      return;
    }
    // Guests get one free 2-minute demo (no login). Deeper formats are gated by
    // the private-beta Invite Code Modal (with waitlist fallback).
    if (length === "overview" && t) {
      navigate(`/demo?topic=${encodeURIComponent(t)}${kidMode ? "&kids=1" : ""}`, { state: { topic: t } });
    } else {
      // CR-040: Early Access modal hidden — send guests to login instead of the waitlist modal.
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen bg-background dojo-page">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border text-grain">
        <div className="max-w-7xl mx-auto px-6 md:px-8 pt-20 pb-24">
          <div className="min-w-0">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 border border-border bg-card px-3 py-1 mb-6 text-xs font-semibold uppercase tracking-[0.2em]" data-testid="hero-badge">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> Stop searching. Start knowing.
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.02] max-w-4xl" data-testid="hero-title">
              {renderEmphasis(landingTitle, "text-primary")}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed" data-testid="hero-subtitle">
              {renderEmphasis(landingSubtitle, "font-bold text-primary")}
            </p>
            <p id="tour-family-safe" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-400" data-testid="hero-family-safe-line">
              <ShieldCheck className="h-4 w-4 shrink-0" />
              Every course is family-safe — safe for learners of all ages, homes, and schools.
            </p>
          </motion.div>

          {/* Search bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-10 max-w-2xl"
          >
            <div className="flex items-stretch border border-foreground bg-card">
              <div className="flex items-center pl-4 text-muted-foreground">
                <Search className="h-5 w-5" />
              </div>
              <Input
                id="tour-landing-search"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && start("overview")}
                placeholder={searchPlaceholder}
                className="border-0 rounded-none h-14 text-base focus-visible:ring-0 shadow-none"
                data-testid="topic-search-input"
              />
              <Button id="tour-try-free" onClick={() => start("overview")} className="rounded-none h-14 px-6 bg-primary hover:opacity-90 gap-2" data-testid="hero-learn-button">
                {user ? "Learn it" : "Try it free"} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              {user ? "Popular: Quantum computing · Stoicism · Black holes · Personal finance"
                    : "Free demo · no signup needed · Popular: Quantum computing · Stoicism · Black holes"}
            </p>
            <button
              id="tour-kid-mode"
              type="button"
              onClick={() => setKidMode((v) => !v)}
              aria-pressed={kidMode}
              className={`mt-4 inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                kidMode
                  ? "border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                  : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
              }`}
              data-testid="kid-mode-toggle"
            >
              <Baby className="h-4 w-4 shrink-0" />
              Ages 8-12 (simplified)
              <span className={`ml-1 h-4 w-7 rounded-full transition-colors relative ${kidMode ? "bg-emerald-500" : "bg-muted-foreground/30"}`}>
                <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${kidMode ? "left-3.5" : "left-0.5"}`} />
              </span>
            </button>
            {kidMode && (
              <p className="mt-1.5 text-xs text-emerald-700/80 dark:text-emerald-400/80" data-testid="kid-mode-hint">
                Your 2-minute overview will use simpler words and kid-friendly examples.
              </p>
            )}
            <button
              id="tour-how-to"
              onClick={() => navigate("/theater")}
              className="group mt-6 w-full sm:w-auto inline-flex items-center gap-3 px-5 h-14 rounded-xl border-2 border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary transition-all duration-300 text-left"
              data-testid="hero-watch-free-button"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-white group-hover:scale-110 transition-transform">
                <Play className="h-5 w-5 ml-0.5" />
              </span>
              <span className="min-w-0">
                <span className="block font-display font-bold text-base text-foreground leading-tight">Watch a course on how to use LearnMeAI</span>
                <span className="block text-xs text-muted-foreground">No signup · start watching in seconds</span>
              </span>
              <ArrowRight className="h-5 w-5 text-primary ml-auto group-hover:translate-x-1 transition-transform" />
            </button>

            {/* Dojo Mode — dedicated serene space */}
            {vibeOn && (
            <div className="mt-10">
              <button
                id="tour-dojo"
                onClick={() => { setDojoMode(true); navigate("/dojo"); }}
                data-testid="enter-dojo-button"
                className="group relative inline-flex items-center gap-3 px-7 h-14 font-display font-bold tracking-tight text-base transition-all duration-500 overflow-hidden bg-[#0B1F2E] text-emerald-50 hover:shadow-[0_0_40px_-10px_rgba(16,185,129,0.6)]"
              >
                <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-r from-emerald-500/0 via-emerald-400/20 to-emerald-500/0" />
                <Sprout className="h-5 w-5 relative text-emerald-400" />
                <span className="relative">Enter the Dojo</span>
              </button>
              <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5" data-testid="dojo-hint">
                <Sparkles className="h-3.5 w-3.5" /> Step into a calm, distraction-free space to learn — pick your scene and let a lesson come to you.
              </p>
            </div>
            )}
          </motion.div>
            </div>
        </div>
      </section>

      {/* Course choices */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-2">Choose your depth</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">One topic. Three ways to learn.</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((p, i) => (
            <motion.button
              key={p.key}
              onClick={() => start(p.key)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              whileHover={{ y: -6 }}
              className="text-left group border border-border bg-card p-8 hover:border-foreground transition-colors"
              data-testid={`course-${p.key}-card`}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="h-12 w-12 bg-secondary group-hover:bg-primary group-hover:text-primary-foreground flex items-center justify-center transition-colors">
                  <p.icon className="h-6 w-6" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{p.time}</span>
              </div>
              <h3 className="font-display text-xl font-bold mb-2">{p.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{p.desc}</p>
              <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                Start now <ArrowRight className="h-4 w-4" />
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 grid md:grid-cols-3 gap-12">
          {[
            { icon: Search, t: "1. Tell us your topic", d: "From quantum physics to sourdough — anything you're curious about." },
            { icon: Play, t: "2. Watch your course", d: "We generate a narrated, chaptered video course in seconds." },
            { icon: History, t: "3. Revisit anytime", d: "Every course is saved to your library to audit on demand." },
          ].map((s, i) => (
            <div key={i} data-testid={`how-step-${i}`}>
              <s.icon className="h-7 w-7 text-primary mb-4" />
              <h3 className="font-display text-xl font-bold mb-2">{s.t}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24 text-center">
        <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter max-w-3xl mx-auto">
          Stop scrolling. Start learning.
        </h2>
        <p className="mt-5 text-muted-foreground text-lg max-w-xl mx-auto">
          Join Learn Me and turn your curiosity into courses today.
        </p>
        <Button onClick={() => user ? navigate("/learn") : navigate("/login")} className="mt-8 rounded-none h-12 px-8 bg-primary hover:opacity-90 text-base" data-testid="cta-button">
          {user ? "Create a course" : "Get started free"}
        </Button>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row justify-between items-center gap-4">
          <span>© 2026 Learn Me. Learn anything, your way.</span>
          <div className="flex items-center gap-6">
            <a href="/terms" className="hover:text-foreground transition-colors" data-testid="footer-terms">Terms of Service</a>
            <a href="/privacy" className="hover:text-foreground transition-colors" data-testid="footer-privacy">Privacy Policy</a>
            <a id="tour-ethics" href="/acceptable-use" className="hover:text-foreground transition-colors" data-testid="footer-acceptable-use">Acceptable Use</a>
            <span className="font-display font-semibold text-foreground">Learn Me</span>
          </div>
        </div>
      </footer>
      <EarlyAccessModal open={earlyOpen} onOpenChange={setEarlyOpen} redirectParams={pendingParams} />
      <GuidedTour steps={LANDING_TOUR} storageKey="lm_tour_landing_v1" />
    </div>
  );
}
